import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleCategoryClick = () => {
    setDropdownOpen(false);
    setMobileCategoryOpen(false);
    setMobileOpen(false);
  };

  const textColor = scrolled ? 'text-foreground-950' : 'text-background-50';
  const textMuted = scrolled ? 'text-foreground-700' : 'text-background-50/85';
  const textHover = scrolled ? 'hover:text-foreground-950' : 'hover:text-background-50';
  const bgClass = scrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60' : 'bg-transparent';
  const logoBg = scrolled ? 'bg-primary-500' : 'bg-background-50/25 backdrop-blur-sm';
  const logoText = scrolled ? 'text-background-50' : 'text-background-50';
  const ctaBg = scrolled ? 'bg-primary-500 hover:bg-primary-600' : 'bg-background-50 hover:bg-background-100';
  const ctaText = scrolled ? 'text-background-50' : 'text-foreground-950';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}>
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 no-underline">
            <div className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg transition-colors duration-300 ${logoBg}`}>
              <i className={`ri-file-list-3-line text-lg ${logoText}`}></i>
            </div>
            <span className={`text-base md:text-lg font-bold tracking-tight transition-colors duration-300 ${textColor}`}>AcBuy Spreadsheet</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={`text-sm font-semibold transition-colors whitespace-nowrap ${textColor}`}>
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${textColor}`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 shadow-sm max-h-96 overflow-y-auto">
                  <div className="px-4 py-2 border-b border-background-200/40 mb-1">
                    <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">14 Product Categories</span>
                  </div>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleCategoryClick}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-background-100">
                        <i className={`${cat.icon} text-base text-primary-500`}></i>
                      </div>
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                        <p className="text-xs text-foreground-500 truncate">{cat.description}</p>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/acbuy-spreadsheet-guide" className={`text-sm font-semibold transition-colors whitespace-nowrap ${textColor}`}>
              Shopping Guide
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-semibold px-5 py-2.5 rounded-full transition-all whitespace-nowrap cursor-pointer ${ctaBg} ${ctaText}`}
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/15'}`}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60">
          <div className="px-4 py-5 space-y-1">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-home-4-line text-primary-600"></i>
              </div>
              Home
            </Link>

            <div>
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="flex items-center justify-between w-full px-3 py-3 rounded-xl text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className="ri-grid-line text-primary-600"></i>
                  </div>
                  Categories
                </div>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCategoryOpen && (
                <div className="ml-3 mt-1 space-y-0.5 border-l-2 border-background-200 pl-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleCategoryClick}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors"
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200">
                        <i className={`${cat.icon} text-xs text-primary-500`}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/acbuy-spreadsheet-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-book-open-line text-primary-600"></i>
              </div>
              Shopping Guide
            </Link>

            <div className="pt-2">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}